<script setup>
// One Stage canonical Table primitive.
// Renders a tokenized table with:
//   - micro-tracked uppercase header
//   - calm blue-tinted row hover
//   - skeleton loading state
//   - empty state via slot
//   - density modes (comfortable / compact)
//   - rounded-card frame w/ hairline border
//
// Two consumption modes:
//   1. Data-driven (props): pass `columns` + `rows`.
//   2. Slot-driven: provide #header and #body for full control.

import { computed, useSlots, useAttrs } from 'vue';
import Skeleton from '../skeleton/Skeleton.vue';

const props = defineProps({
  /** Column definitions. Each: { key, label, width?, align? } */
  columns: {
    type: Array,
    default: () => [],
  },
  /** Row data. Each row is keyed by column.key. */
  rows: {
    type: Array,
    default: () => [],
  },
  /** Row density. */
  density: {
    type: String,
    default: 'comfortable',
    validator: v => ['comfortable', 'compact'].includes(v),
  },
  /** Show skeleton rows instead of data. */
  loading: { type: Boolean, default: false },
  /** Number of skeleton rows when loading. */
  loadingRows: { type: Number, default: 6 },
  /** Disable the rounded frame (use inside a Panel). */
  bare: { type: Boolean, default: false },
  /** Sticky header row. */
  stickyHeader: { type: Boolean, default: false },
  /** Optional row key fn. */
  rowKey: { type: [String, Function], default: 'id' },
});

const emit = defineEmits(['rowClick']);
const slots = useSlots();
const attrs = useAttrs();

const cellPadY = computed(() => (props.density === 'compact' ? 'py-2.5' : 'py-3'));
const cellPadX = 'px-4';

const isEmpty = computed(() => !props.loading && props.rows.length === 0);

// Only signal clickability when the consumer actually listens for rowClick.
const isRowClickable = computed(() => 'onRowClick' in attrs);

// Safe colspan for empty/skeleton rows: collapse to the column count, or 1 if
// the consumer is in slot-driven mode and didn't pass columns.
const fullSpan = computed(() => props.columns.length || 1);

const getRowKey = row => {
  if (typeof props.rowKey === 'function') return props.rowKey(row);
  return row[props.rowKey];
};

const onRowClick = (row, e) => {
  if (isRowClickable.value) emit('rowClick', { row, event: e });
};
</script>

<template>
  <div
    :class="[
      'os-tx-shadow w-full overflow-x-auto',
      !bare && 'bg-n-solid-1 dark:bg-n-solid-2 border border-n-weak os-r-xl os-elev-1',
    ]"
  >
    <table class="w-full border-collapse">
      <thead
        :class="[
          'bg-n-alpha-2',
          stickyHeader && 'sticky top-0 z-10',
        ]"
      >
        <slot name="header">
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :style="col.width ? { width: col.width } : undefined"
              :class="[
                cellPadX,
                'py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-n-slate-11 border-b border-n-weak whitespace-nowrap',
                col.align === 'right' && 'text-right',
                col.align === 'center' && 'text-center',
              ]"
            >
              {{ col.label }}
            </th>
          </tr>
        </slot>
      </thead>

      <tbody>
        <!-- Loading skeleton rows -->
        <template v-if="loading">
          <tr v-for="i in loadingRows" :key="`skel-${i}`" class="border-b border-n-weak last:border-0">
            <td
              v-for="col in columns.length || 4"
              :key="`skel-${i}-${col}`"
              :class="[cellPadX, cellPadY]"
            >
              <Skeleton variant="line" :width="`${40 + (col * 9 + i * 7) % 50}%`" />
            </td>
          </tr>
        </template>

        <!-- Empty state -->
        <tr v-else-if="isEmpty">
          <td :colspan="fullSpan" class="px-6 py-12">
            <slot name="empty">
              <div class="text-center text-sm text-n-slate-11">No records found.</div>
            </slot>
          </td>
        </tr>

        <!-- Body slot for full control -->
        <template v-else-if="slots.body">
          <slot name="body" />
        </template>

        <!-- Data-driven rows -->
        <template v-else>
          <tr
            v-for="row in rows"
            :key="getRowKey(row)"
            :class="[
              'border-b border-n-weak last:border-0 os-tx-colors hover:bg-n-brand/[0.04] dark:hover:bg-n-brand/[0.08]',
              isRowClickable ? 'cursor-pointer' : 'cursor-default',
            ]"
            @click="onRowClick(row, $event)"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              :class="[
                cellPadX,
                cellPadY,
                'text-sm text-n-slate-12 align-middle',
                col.align === 'right' && 'text-right',
                col.align === 'center' && 'text-center',
              ]"
            >
              <slot :name="`cell-${col.key}`" :row="row" :value="row[col.key]" :column="col">
                {{ row[col.key] }}
              </slot>
            </td>
          </tr>
        </template>
      </tbody>

      <tfoot v-if="slots.footer">
        <slot name="footer" />
      </tfoot>
    </table>
  </div>
</template>
